/**
 * Pipeline tab — Case Setup and the live run view.
 *
 * New Case / Import Data / Open Case all land here. The draft lives in
 * `@/pipeline/session`; the open case and job live in the shell store.
 */

import { useCallback, useEffect, useRef } from "react";
import {
  FolderOpen,
  Play,
  RefreshCw,
  Save,
  Square,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Progress,
  Select,
  StatusDot,
  Table,
  Td,
  Th,
} from "@/components/ui/primitives";
import { pickDirectory } from "@/ipc/api";
import type { DetectedFile, PipelineKind, Severity } from "@/ipc/contract";
import { cn, formatBytes, formatDuration } from "@/lib/utils";
import {
  completedStageCount,
  currentStageStatus,
  estimateEtaSec,
  jobElapsedSec,
  latestStatusLine,
  weightedProgressPct,
} from "@/pipeline/progress";
import { applyFilter, FILTER_PRESETS, matchingFiles, presetIdFor } from "@/pipeline/filter";
import { containerSubjectPath, deriveMounts, inputRootFor } from "@/pipeline/paths";
import { checkDependencies, fullSelection } from "@/pipeline/stages";
import { usePipelineSession } from "@/pipeline/session";
import { STAGE_LABEL, StageGlyph,
  StageHealthNote,
} from "@/pipeline/status";
import { ensureStageTimings, useStageTimings } from "@/pipeline/timings";
import { useNow } from "@/pipeline/useNow";
import { useAppStore, useCurrentCase, useJob, useLogLines, useSystemStatus } from "@/store/appStore";

ensureStageTimings();

const KIND_LABEL: Record<PipelineKind, string> = {
  dwi: "DWI",
  pwi: "PWI",
  combined: "DWI + PWI",
};

const SEVERITY_TONE: Record<Severity, "green" | "amber" | "red"> = {
  ok: "green",
  warning: "amber",
  error: "red",
};

export function PipelineTab() {
  const mode = usePipelineSession((s) => s.mode);
  return mode === "running" ? <RunView /> : <SetupView />;
}

// --------------------------------------------------------------- setup ---

function SetupView() {
  const draft = usePipelineSession((s) => s.draft);
  const scan = usePipelineSession((s) => s.scan);
  const scanning = usePipelineSession((s) => s.scanning);
  const scanError = usePipelineSession((s) => s.scanError);
  const busy = usePipelineSession((s) => s.busy);
  const notice = usePipelineSession((s) => s.notice);
  const patchDraft = usePipelineSession((s) => s.patchDraft);
  const setSubjectId = usePipelineSession((s) => s.setSubjectId);
  const setPipelineKind = usePipelineSession((s) => s.setPipelineKind);
  const rescan = usePipelineSession((s) => s.rescan);
  const chooseDataDir = usePipelineSession((s) => s.chooseDataDir);
  const chooseDatasetRoot = usePipelineSession((s) => s.chooseDatasetRoot);
  const setDataMode = usePipelineSession((s) => s.setDataMode);
  const setFilter = usePipelineSession((s) => s.setFilter);
  const focusSubject = usePipelineSession((s) => s.focusSubject);
  const toggleMatchedSubject = usePipelineSession((s) => s.toggleMatchedSubject);
  const selectAllMatched = usePipelineSession((s) => s.selectAllMatched);
  const dataMode = usePipelineSession((s) => s.dataMode);
  const datasetRoot = usePipelineSession((s) => s.datasetRoot);
  const subjectGlob = usePipelineSession((s) => s.subjectGlob);
  const fileGlob = usePipelineSession((s) => s.fileGlob);
  const datasetSubjects = usePipelineSession((s) => s.datasetSubjects);
  const datasetScanning = usePipelineSession((s) => s.datasetScanning);
  const datasetError = usePipelineSession((s) => s.datasetError);
  const selectedSubjectIds = usePipelineSession((s) => s.selectedSubjectIds);
  const saveSetup = usePipelineSession((s) => s.saveSetup);
  const createCaseFromDraft = usePipelineSession((s) => s.createCaseFromDraft);
  const run = usePipelineSession((s) => s.run);
  const setNotice = usePipelineSession((s) => s.setNotice);
  const currentCase = useCurrentCase();
  const system = useSystemStatus();
  const selectedStageKey = usePipelineSession((s) => s.selectedStageKey);
  const togglePanel = useAppStore((s) => s.togglePanel);

  // Landing here straight from Home's "Run" card, Properties has nothing to
  // show yet -- it only fills once a stage is selected (see `meta` below in
  // `PipelineProperties`) -- so by default it is a panel-width of "Nothing
  // selected" next to a setup form someone is trying to fill in. Tuck it
  // away on that first, truly empty arrival; once a case exists or a stage
  // is picked, leave the reader's own panel choice alone from then on.
  const collapsedEmptyProperties = useRef(false);
  useEffect(() => {
    if (collapsedEmptyProperties.current) return;
    if (currentCase || selectedStageKey) return;
    collapsedEmptyProperties.current = true;
    togglePanel("right", true);
  }, [currentCase, selectedStageKey, togglePanel]);

  const browseSubject = useCallback(async () => {
    const { path } = await pickDirectory("Select a subject folder", draft.subjectDir || undefined);
    if (path) await chooseDataDir(path);
  }, [chooseDataDir, draft.subjectDir]);

  const browseDataset = useCallback(async () => {
    const { path } = await pickDirectory(
      "Select a subjects directory",
      datasetRoot || draft.subjectDir || undefined,
    );
    if (path) await chooseDatasetRoot(path);
  }, [chooseDatasetRoot, datasetRoot, draft.subjectDir]);

  const browsePwi = useCallback(async () => {
    const { path } = await pickDirectory("Select the PWI subject folder", draft.pwiSubjectDir || undefined);
    if (path) patchDraft({ pwiSubjectDir: path });
  }, [draft.pwiSubjectDir, patchDraft]);

  const browseOutput = useCallback(async () => {
    const { path } = await pickDirectory("Select an output folder", draft.outputRoot || undefined);
    if (path) patchDraft({ outputRoot: path });
  }, [draft.outputRoot, patchDraft]);

  const matchedSubjects = applyFilter(datasetSubjects, subjectGlob, fileGlob);
  const selectedMatched = matchedSubjects.filter((s) => selectedSubjectIds.includes(s.subjectId));
  const filterPreset = presetIdFor(subjectGlob, fileGlob);

  const readiness = scan?.readiness[draft.pipelineKind];
  const blocked = Boolean(scan?.isDatasetRoot) && dataMode !== "dataset";
  const canRun =
    Boolean(draft.outputRoot && (draft.stages.dwi.length || draft.stages.pwi.length)) &&
    !blocked &&
    !busy &&
    (dataMode === "dataset" ? selectedMatched.length > 0 : Boolean(draft.subjectDir));

  const issues = [
    ...checkDependencies("DWI", draft.stages.dwi),
    ...checkDependencies("PWI", draft.stages.pwi),
  ];
  const hardError = issues.some((i) => i.severity === "error");

  const mounts = deriveMounts(
    inputRootFor(draft.subjectDir),
    draft.outputRoot,
    system?.engine.kind ?? "docker",
  );
  const gpuDevices = system?.gpu.devices ?? [];
  const images = system?.docker.images ?? [];

  return (
    <div className="h-full overflow-auto bg-ads-bg p-3">
      {notice ? (
        <div
          className={cn(
            "mb-3 flex items-start justify-between gap-3 rounded border px-3 py-2 text-xs",
            notice.tone === "error"
              ? "border-red-200 bg-red-50 text-red-800"
              : "border-ads-accent-border bg-ads-accent-soft text-ads-text",
          )}
        >
          <span>
            {notice.message}
            {notice.detail ? (
              <span className="mt-1 block font-mono text-[11px] opacity-70">{notice.detail}</span>
            ) : null}
          </span>
          <button type="button" className="shrink-0 underline" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-12 gap-3">
        <Card className="col-span-12">
          <CardHeader>
            <CardTitle>1. Protocol</CardTitle>
            <span className="text-[11px] text-neutral-400">
              A fixed OpenADS sequence — not a toolbox. Image edits stay on Study.
            </span>
          </CardHeader>
          <CardBody className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { id: "dwi" as const, label: "DWI stroke core", hint: "Prep → mask → register → lesion → report" },
                  { id: "pwi" as const, label: "PWI hypoperfusion", hint: "Prep → TTP → coreg → HP → report" },
                  { id: "combined" as const, label: "DWI + PWI", hint: "Core, HP and mismatch together" },
                ] as const
              ).map((preset) => {
                const active = draft.pipelineKind === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => {
                      setPipelineKind(preset.id);
                      usePipelineSession.getState().selectAllStages();
                    }}
                    className={cn(
                      "min-w-[10rem] flex-1 rounded border px-3 py-2 text-left",
                      active
                        ? "border-ads-accent-border bg-ads-accent-soft"
                        : "border-ads-border hover:bg-neutral-50",
                    )}
                  >
                    <div className="text-xs font-semibold text-ads-text">{preset.label}</div>
                    <div className="text-[11px] text-neutral-500">{preset.hint}</div>
                  </button>
                );
              })}
            </div>
            {(() => {
              const full = fullSelection(draft.pipelineKind);
              const custom =
                JSON.stringify(draft.stages.dwi) !== JSON.stringify(full.dwi) ||
                JSON.stringify(draft.stages.pwi) !== JSON.stringify(full.pwi);
              return custom ? (
                <p className="text-[11px] text-neutral-500">
                  Custom step list — some default steps are unchecked on the left. Other checked
                  steps stay selected when you open one tool from Study.
                </p>
              ) : (
                <p className="text-[11px] text-neutral-500">
                  Check or uncheck steps on the left. Run protocol executes the checked list.
                </p>
              );
            })()}
          </CardBody>
        </Card>

        <Card className="col-span-12 xl:col-span-7">
          <CardHeader>
            <CardTitle>2. Data</CardTitle>
            <span className="text-[11px] text-neutral-400">
              {dataMode === "dataset"
                ? datasetRoot
                  ? `${matchedSubjects.length} matching`
                  : "Subjects folder"
                : currentCase
                  ? `Open: ${currentCase.subjectId}`
                  : "No case written yet"}
            </span>
          </CardHeader>
          <CardBody className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-wrap gap-1.5 sm:col-span-2">
              {(
                [
                  { id: "subject" as const, label: "One subject" },
                  // Named for what a reader with 200 subjects is looking for.
                  { id: "dataset" as const, label: "Many subjects (batch)" },
                ] as const
              ).map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => setDataMode(mode.id)}
                  className={cn(
                    "rounded border px-2.5 py-1 text-xs",
                    dataMode === mode.id
                      ? "border-ads-accent-border bg-ads-accent-soft font-medium"
                      : "border-ads-border hover:bg-neutral-50",
                  )}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            {dataMode === "dataset" ? (
              <>
                <Field
                  label="Subjects directory"
                  hint="Parent folder of many subjects. The filter below is applied immediately."
                  className="sm:col-span-2"
                >
                  <div className="flex gap-1.5">
                    <Input
                      value={datasetRoot}
                      onChange={(e) => usePipelineSession.setState({ datasetRoot: e.target.value })}
                      placeholder="/data/subjects"
                    />
                    <Button type="button" onClick={() => void browseDataset()}>
                      <FolderOpen className="h-3.5 w-3.5" />
                      Browse
                    </Button>
                  </div>
                </Field>
                <Field label="Filter template" hint="Folder glob. * and ? allowed.">
                  <Select
                    value={filterPreset}
                    onChange={(e) => {
                      const preset = FILTER_PRESETS.find((p) => p.id === e.target.value);
                      if (preset) setFilter({ preset });
                    }}
                  >
                    {FILTER_PRESETS.map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {preset.label}
                      </option>
                    ))}
                    {filterPreset === "custom" ? <option value="custom">Custom</option> : null}
                  </Select>
                </Field>
                <Field label="Folder glob" hint="Matches the subject folder name.">
                  <Input
                    value={subjectGlob}
                    onChange={(e) => setFilter({ subjectGlob: e.target.value })}
                    placeholder="sub-*"
                  />
                </Field>
                <Field
                  label="Required file"
                  hint="Empty = any NIfTI. Comma = any of. {id} = folder name."
                  className="sm:col-span-2"
                >
                  <Input
                    value={fileGlob}
                    onChange={(e) => setFilter({ fileGlob: e.target.value })}
                    placeholder="*_DWI.nii.gz"
                  />
                </Field>
                <ParallelField className="sm:col-span-2" />
                <div className="sm:col-span-2 rounded border border-ads-border">
                  <div className="flex items-center justify-between gap-2 border-b border-ads-border px-2 py-1.5 text-[11px] text-ads-muted">
                    <span>
                      {datasetScanning
                        ? "Scanning…"
                        : `${selectedMatched.length} selected · ${matchedSubjects.length} match · ${datasetSubjects.length} folders`}
                    </span>
                    <button
                      type="button"
                      className="underline"
                      onClick={() => selectAllMatched(matchedSubjects.map((s) => s.subjectId))}
                    >
                      Select all matches
                    </button>
                  </div>
                  {datasetError ? (
                    <p className="px-2 py-2 text-xs text-red-600">{datasetError}</p>
                  ) : matchedSubjects.length === 0 ? (
                    <p className="px-2 py-2 text-xs text-ads-muted">
                      {datasetRoot
                        ? "No folders match this template. Adjust the globs — the list updates as you type."
                        : "Browse a subjects directory to list matches."}
                    </p>
                  ) : (
                    <div className="max-h-48 overflow-auto">
                      {matchedSubjects.map((subject) => {
                        const checked = selectedSubjectIds.includes(subject.subjectId);
                        const focused = draft.subjectDir === subject.dir;
                        const hits = matchingFiles(subject, fileGlob);
                        return (
                          <div
                            key={subject.dir}
                            className={cn(
                              "flex items-center gap-2 px-2 py-1 text-xs",
                              focused ? "bg-ads-accent-soft" : "hover:bg-neutral-50",
                            )}
                          >
                            <Checkbox
                              checked={checked}
                              onChange={() => toggleMatchedSubject(subject.subjectId)}
                            />
                            <button
                              type="button"
                              className="min-w-0 flex-1 truncate text-left"
                              onClick={() => void focusSubject(subject.dir)}
                            >
                              <span className="font-medium text-ads-text">{subject.subjectId}</span>
                              <span className="ml-2 text-[11px] text-ads-muted">
                                {hits.length ? hits.slice(0, 3).join(", ") : `${subject.fileCount} files`}
                              </span>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Field label="Subject folder" hint="Folder that holds the NIfTI files for one subject." className="sm:col-span-2">
                <div className="flex gap-1.5">
                  <Input
                    value={draft.subjectDir}
                    onChange={(e) => patchDraft({ subjectDir: e.target.value })}
                    placeholder="/data/sub-001"
                  />
                  <Button type="button" onClick={() => void browseSubject()}>
                    <FolderOpen className="h-3.5 w-3.5" />
                    Browse
                  </Button>
                </div>
              </Field>
            )}

            {draft.pipelineKind === "combined" ? (
              <Field label="PWI subject folder" hint="Leave empty if PWI files sit next to the DWI files." className="sm:col-span-2">
                <div className="flex gap-1.5">
                  <Input
                    value={draft.pwiSubjectDir}
                    onChange={(e) => patchDraft({ pwiSubjectDir: e.target.value })}
                    placeholder="/data/pwi/sub-001"
                  />
                  <Button type="button" onClick={() => void browsePwi()}>
                    <FolderOpen className="h-3.5 w-3.5" />
                    Browse
                  </Button>
                </div>
              </Field>
            ) : null}

            <Field label="Output folder" hint="case.json and pipeline products land under <output>/<subjectId>/." className="sm:col-span-2">
              <div className="flex gap-1.5">
                <Input
                  value={draft.outputRoot}
                  onChange={(e) => patchDraft({ outputRoot: e.target.value })}
                  placeholder="/out"
                />
                <Button type="button" onClick={() => void browseOutput()}>
                  <FolderOpen className="h-3.5 w-3.5" />
                  Browse
                </Button>
              </div>
            </Field>

            <Field label="Subject ID">
              <Input value={draft.subjectId} onChange={(e) => setSubjectId(e.target.value)} />
            </Field>
            <Field label="Study name">
              <Input
                value={draft.studyName}
                onChange={(e) => patchDraft({ studyName: e.target.value })}
                placeholder="Optional"
              />
            </Field>

            <Field label="Protocol family">
              <Select
                value={draft.pipelineKind}
                onChange={(e) => setPipelineKind(e.target.value as PipelineKind)}
              >
                <option value="dwi">DWI — stroke core</option>
                <option value="pwi">PWI — hypoperfusion</option>
                <option value="combined">DWI + PWI combined</option>
              </Select>
            </Field>
            <Field label="GPU">
              <Select value={draft.gpuIndex} onChange={(e) => patchDraft({ gpuIndex: e.target.value })}>
                {gpuDevices.length === 0 ? <option value="0">0</option> : null}
                {gpuDevices.map((d) => (
                  <option key={d.index} value={String(d.index)}>
                    {d.index} — {d.name}
                  </option>
                ))}
                <option value="cpu">CPU</option>
              </Select>
            </Field>

            <Field label="Docker image" hint="Automatic picks the GPU image when the nvidia runtime is present.">
              <Select
                value={draft.dockerImage ?? ""}
                onChange={(e) => patchDraft({ dockerImage: e.target.value || null })}
              >
                <option value="">Automatic</option>
                {images.map((img) => (
                  <option key={img.reference} value={img.reference}>
                    {img.reference}
                  </option>
                ))}
              </Select>
            </Field>
            <label className="flex items-center gap-2 self-end pb-1 text-xs text-neutral-700">
              <Checkbox
                checked={draft.cleanupEnabled}
                onChange={(e) => patchDraft({ cleanupEnabled: e.target.checked })}
              />
              Delete intermediate files when the job finishes
            </label>

            <div className="flex flex-wrap gap-1.5 sm:col-span-2">
              <Button type="button" onClick={() => void rescan()} disabled={!draft.subjectDir || scanning}>
                <RefreshCw className={cn("h-3.5 w-3.5", scanning && "animate-spin")} />
                Validate Data
              </Button>
              <Button type="button" onClick={() => void saveSetup()} disabled={!currentCase || busy}>
                <Save className="h-3.5 w-3.5" />
                Save Case
              </Button>
              <Button type="button" onClick={() => void createCaseFromDraft()} disabled={!draft.subjectDir || busy}>
                Create Case
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => void run()}
                disabled={!canRun || hardError}
              >
                <Play className="h-3.5 w-3.5" />
                {dataMode === "dataset" && selectedMatched.length > 1
                  ? `Run protocol (${selectedMatched.length})`
                  : "Run protocol"}
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card className="col-span-12 xl:col-span-5">
          <CardHeader>
            <CardTitle>Readiness</CardTitle>
            {scan ? (
              <span className="text-[11px] text-neutral-400">
                {scan.validCount} ok · {scan.warningCount} warn · {scan.errorCount} error
              </span>
            ) : null}
          </CardHeader>
          <CardBody>
            {!draft.subjectDir && dataMode !== "dataset" ? (
              <EmptyState title="No subject folder" hint="Browse to a subject directory to scan its NIfTI files." />
            ) : dataMode === "dataset" && !datasetRoot ? (
              <EmptyState
                title="No subjects directory"
                hint="Browse to the parent folder, then set a filter template. Matching subjects appear immediately."
              />
            ) : scanning || datasetScanning ? (
              <p className="text-xs text-neutral-500">Scanning…</p>
            ) : scanError ? (
              <p className="text-xs text-red-600">{scanError}</p>
            ) : scan ? (
              <div className="space-y-2 text-xs">
                {scan.isDatasetRoot && dataMode !== "dataset" ? (
                  <p className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-red-800">
                    This looks like a subjects directory. Switch to Subjects folder, or pick one subject inside it.
                  </p>
                ) : null}
                {(["dwi", "pwi", "combined"] as PipelineKind[]).map((kind) => {
                  const r = scan.readiness[kind];
                  return (
                    <div key={kind} className="flex items-start justify-between gap-2">
                      <span className="flex items-center gap-1.5">
                        <StatusDot tone={r.ready ? "green" : "amber"} />
                        {KIND_LABEL[kind]}
                      </span>
                      <span className="text-right text-neutral-500">
                        {r.ready
                          ? r.notes[0] ?? "Ready"
                          : r.missing.length
                            ? `Missing ${r.missing.join(", ")}`
                            : r.notes[0] ?? "Not ready"}
                      </span>
                    </div>
                  );
                })}
                {readiness && !readiness.ready ? (
                  <p className="text-amber-700">
                    The {KIND_LABEL[draft.pipelineKind]} pipeline is not ready. You can still create the case;
                    Run stays disabled until the missing inputs are present.
                  </p>
                ) : null}
                {issues.map((issue) => (
                  <p
                    key={`${issue.modality}:${issue.stage}:${issue.missing}`}
                    className={issue.severity === "error" ? "text-red-700" : "text-amber-700"}
                  >
                    {issue.message}
                  </p>
                ))}
                <div className="border-t border-neutral-100 pt-2 text-[11px] text-neutral-500">
                  <div>Container subject: {containerSubjectPath(draft.subjectDir)}</div>
                  {mounts.map((m) => (
                    <div key={m.containerPath}>
                      {m.containerPath} ← {m.hostPath} ({m.access})
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState title="Not scanned yet" hint="Click Validate Data after choosing a folder." />
            )}
          </CardBody>
        </Card>

        <Card className="col-span-12">
          <CardHeader>
            <CardTitle>Detected Files</CardTitle>
          </CardHeader>
          {!scan || scan.files.length === 0 ? (
            <EmptyState title="No files detected" hint="A subject folder typically holds *_DWI.nii.gz and friends." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>File</Th>
                  <Th>Type</Th>
                  <Th>Modality</Th>
                  <Th>Dimensions</Th>
                  <Th>Size</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {scan.files.map((file) => (
                  <FileRow key={file.path} file={file} />
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}

function FileRow({ file }: { file: DetectedFile }) {
  return (
    <tr>
      <Td>
        <div className="font-medium text-neutral-800">{file.fileName}</div>
        <div className="text-[11px] text-neutral-400">{file.relPath}</div>
      </Td>
      <Td>{file.fileType}</Td>
      <Td>{file.modality}</Td>
      <Td className="whitespace-nowrap font-mono text-xs">
        {file.dimensions?.join(" × ") ?? "—"}
      </Td>
      <Td className="whitespace-nowrap">{formatBytes(file.sizeBytes)}</Td>
      <Td>
        <Badge tone={SEVERITY_TONE[file.status]}>{file.detail}</Badge>
      </Td>
    </tr>
  );
}

// ---------------------------------------------------------------- run ---

function RunView() {
  const job = useJob();
  const lines = useLogLines();
  const timings = useStageTimings((s) => s.timings);
  const now = useNow(1000, job?.state === "running");
  const stop = usePipelineSession((s) => s.stop);
  const setMode = usePipelineSession((s) => s.setMode);
  const setNotice = usePipelineSession((s) => s.setNotice);
  const notice = usePipelineSession((s) => s.notice);
  const currentCase = useCurrentCase();

  const running = job?.state === "running" || job?.state === "queued";
  // Scored against the engine that is actually running it: the GPU and CPU
  // weight tables are near-inverses of each other.
  const deviceClass = job?.deviceClass;
  const pct = weightedProgressPct(job, timings, now, deviceClass);
  const eta = estimateEtaSec(job, timings, now);
  const elapsed = jobElapsedSec(job, now);
  const current = currentStageStatus(job);
  const statusLine = latestStatusLine(lines);
  const done = completedStageCount(job);
  const total = job?.stages.length ?? 0;

  return (
    <div className="flex h-full flex-col overflow-auto bg-ads-bg p-3">
      {notice ? (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {notice.message}
          <button type="button" className="ml-2 underline" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{currentCase ? `${currentCase.subjectId} — ${KIND_LABEL[currentCase.pipelineKind]}` : "Pipeline"}</CardTitle>
          <span className="text-[11px] text-neutral-400">
            {job ? `${done} / ${total} stages · ${job.state}` : "No job"}
          </span>
        </CardHeader>
        <CardBody className="space-y-3">
          <Progress value={pct} className="h-2" />
          <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
            <Stat label="Elapsed" value={formatDuration(elapsed)} />
            <Stat label="ETA" value={formatDuration(eta)} />
            <Stat label="Progress" value={`${pct}%`} />
            <Stat
              label="Current"
              value={current ? `${current.modality} · ${current.title}` : "—"}
            />
          </div>
          {statusLine ? <p className="font-mono text-[11px] text-neutral-600">{statusLine}</p> : null}
          {/* Only ever present while a stage is silent; see health.rs. */}
          <StageHealthNote health={job?.health ?? null} />
          <div className="flex flex-wrap gap-1.5">
            <Button type="button" variant="danger" onClick={() => void stop()} disabled={!running}>
              <Square className="h-3.5 w-3.5" />
              Stop
            </Button>
            <Button type="button" onClick={() => setMode("setup")}>
              Back to protocol
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card className="mt-3">
        <CardHeader>
          <CardTitle>Stages</CardTitle>
        </CardHeader>
        {!job ? (
          <EmptyState title="No job" hint="Run protocol from the setup page to see stage progress." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Stage</Th>
                <Th>Modality</Th>
                <Th>State</Th>
              </tr>
            </thead>
            <tbody>
              {job.stages.map((stage) => (
                <tr key={`${stage.modality}:${stage.id}`}>
                  <Td>
                    <span className="inline-flex items-center gap-1.5">
                      <StageGlyph state={stage.state} />
                      {stage.title}
                    </span>
                  </Td>
                  <Td>{stage.modality}</Td>
                  <Td>{STAGE_LABEL[stage.state]}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <p className="mt-3 text-[11px] text-neutral-400">
        Stage checkboxes on the left still apply to the next run. Live output is in the Log dock.
      </p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] text-neutral-400">{label}</div>
      <div className="font-medium text-neutral-800">{value}</div>
    </div>
  );
}

export default PipelineTab;

/**
 * How many subjects run at once.
 *
 * The number is decided on the Rust side from the machine's memory, because that
 * is the constraint that bites: one run peaks at 8.4 GB on Metal and 21 GB on the
 * CPU, both measured, so a 24 GB machine holds two with Metal on and one without.
 * The reason is shown rather than just the number — "2" on its own invites the
 * question, and someone whose machine says 1 deserves to know it is the memory
 * and that Metal is the lever.
 *
 * An explicit choice is obeyed even above what memory suggests. It is the
 * reader's own machine, and the cost is stated rather than enforced.
 */
function ParallelField({ className }: { className?: string }) {
  const status = useSystemStatus();
  const prefs = useAppStore((s) => s.preferences);
  const concurrency = status?.concurrency;
  const current = prefs?.maxParallelJobs ?? null;

  const choose = async (value: number | null) => {
    await useAppStore.getState().updatePreferences({ maxParallelJobs: value });
    // The limit itself comes from the status, which the Rust side recomputes from
    // the new preference — forced, or the cached reading would still say the old
    // number while the engine had already moved on.
    await useAppStore.getState().refreshSystemStatus(true);
  };

  return (
    <Field
      label="Run at once"
      hint={concurrency?.reason ?? "Reading the machine…"}
      className={className}
    >
      <Select
        value={current === null ? "auto" : String(current)}
        disabled={!prefs}
        onChange={(e) => void choose(e.target.value === "auto" ? null : Number(e.target.value))}
      >
        <option value="auto">
          Automatic{concurrency ? ` — ${concurrency.automatic} at a time` : ""}
        </option>
        {[1, 2, 3, 4, 6, 8].map((n) => (
          <option key={n} value={n}>
            {n} subject{n === 1 ? "" : "s"} at a time
            {concurrency && n > concurrency.automatic ? " (more than memory suggests)" : ""}
          </option>
        ))}
      </Select>
    </Field>
  );
}
